import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { saveOrderState, loadOrderState, clearAllData } from '../services/storageService';
import { calculateOrderTotals, getItemLineTotal } from '../utils/priceCalculation';

// Order state management
const initialState = {
    items: [],
    customerInfo: {
        name: '',
        phone: '',
        email: '',
        specialInstructions: ''
    },
    currentStep: 'welcome',
    currentCategory: null,
    currentItem: null,
    currentModifiers: {},
    subtotal: 0,
    tax: 0,
    total: 0,
    isLoading: false,
    error: null
};

// Action types
export const ORDER_ACTIONS = {
    SET_STEP: 'SET_STEP',
    SET_CATEGORY: 'SET_CATEGORY',
    SET_ITEM: 'SET_ITEM',
    ADD_ITEM: 'ADD_ITEM',
    REMOVE_ITEM: 'REMOVE_ITEM',
    UPDATE_ITEM: 'UPDATE_ITEM',
    SET_CUSTOMER_INFO: 'SET_CUSTOMER_INFO',
    SET_MODIFIERS: 'SET_MODIFIERS',
    CALCULATE_TOTALS: 'CALCULATE_TOTALS',
    SET_LOADING: 'SET_LOADING',
    SET_ERROR: 'SET_ERROR',
    RESET_ORDER: 'RESET_ORDER',
    CLEAR_CART: 'CLEAR_CART'
};

// Reducer function
function orderReducer(state, action) {
    switch (action.type) {
        case ORDER_ACTIONS.SET_STEP:
            return { ...state, currentStep: action.payload };

        case ORDER_ACTIONS.SET_CATEGORY:
            return { ...state, currentCategory: action.payload };

        case ORDER_ACTIONS.SET_ITEM:
            return { ...state, currentItem: action.payload };

        case ORDER_ACTIONS.ADD_ITEM: {
            const newItems = [...state.items, action.payload];
            return { ...state, items: newItems };
        }

        case ORDER_ACTIONS.REMOVE_ITEM:
            return {
                ...state,
                items: state.items.filter((_, index) => index !== action.payload)
            };

        case ORDER_ACTIONS.UPDATE_ITEM: {
            const updatedItems = state.items.map((item, index) =>
                index === action.payload.index ? action.payload.item : item
            );
            return { ...state, items: updatedItems };
        }

        case ORDER_ACTIONS.SET_CUSTOMER_INFO:
            return {
                ...state,
                customerInfo: { ...state.customerInfo, ...action.payload }
            };

        case ORDER_ACTIONS.SET_MODIFIERS:
            return { ...state, currentModifiers: action.payload };

        case ORDER_ACTIONS.CALCULATE_TOTALS: {
            // Use stored pricing for accurate totals (chatbot items already have correct pricing)
            const itemsWithStoredPricing = state.items.map(item => ({
                ...item,
                currentPrice: getItemLineTotal(item, false)  // Use stored price, not current menu pricing
            }));
            const { subtotal, tax, total } = calculateOrderTotals(itemsWithStoredPricing);
            return { ...state, subtotal, tax, total };
        }

        case ORDER_ACTIONS.SET_LOADING:
            return { ...state, isLoading: action.payload };

        case ORDER_ACTIONS.SET_ERROR:
            return { ...state, error: action.payload };

        case ORDER_ACTIONS.RESET_ORDER:
            return { ...initialState };

        case ORDER_ACTIONS.CLEAR_CART:
            return {
                ...state,
                items: [],
                currentCategory: null,
                currentItem: null,
                currentModifiers: {},
                subtotal: 0,
                tax: 0,
                total: 0,
                isLoading: false,
                error: null
                // Note: customerInfo is intentionally preserved
            };

        default:
            return state;
    }
}

// Create context
const OrderContext = createContext();

// Provider component
export function OrderProvider({ children }) {
    // Initialize state with localStorage data if available
    const getInitialState = () => {
        const saved = loadOrderState();
        return saved || initialState;
    };

    const [state, dispatch] = useReducer(orderReducer, getInitialState());

    // Save to localStorage whenever state changes
    useEffect(() => {
        saveOrderState(state);
    }, [state]);

    // Helper functions
    const addItemToOrder = (item) => {
        dispatch({ type: ORDER_ACTIONS.ADD_ITEM, payload: item });
        dispatch({ type: ORDER_ACTIONS.CALCULATE_TOTALS });
    };

    const removeItemFromOrder = (index) => {
        dispatch({ type: ORDER_ACTIONS.REMOVE_ITEM, payload: index });
        dispatch({ type: ORDER_ACTIONS.CALCULATE_TOTALS });
    };

    const updateItemQuantity = (index, newQuantity) => {
        if (newQuantity <= 0) {
            removeItemFromOrder(index);
        } else {
            const currentItem = state.items[index];

            // Calculate modifier total (respect modifier quantities and unitPrice fallback)
            const modifierTotal = currentItem.modifiers?.reduce((sum, mod) => {
                const perUnit = mod.unitPrice != null ? mod.unitPrice : (mod.price || 0);
                const q = mod.quantity || 1;
                return sum + perUnit * q;
            }, 0) || 0;

            // Calculate new total price including modifiers
            const basePrice = currentItem.unitPrice != null ? currentItem.unitPrice : (currentItem.price || 0);
            const newTotalPrice = (basePrice + modifierTotal) * newQuantity;

            const updatedItem = {
                ...currentItem,
                quantity: newQuantity,
                totalPrice: newTotalPrice
            };

            dispatch({
                type: ORDER_ACTIONS.UPDATE_ITEM,
                payload: { index, item: updatedItem }
            });
            dispatch({ type: ORDER_ACTIONS.CALCULATE_TOTALS });
        }
    };

    const updateCustomerInfo = (info) => {
        dispatch({ type: ORDER_ACTIONS.SET_CUSTOMER_INFO, payload: info });
    };

    const setCurrentStep = (step) => {
        dispatch({ type: ORDER_ACTIONS.SET_STEP, payload: step });
    };

    const setCurrentCategory = (category) => {
        dispatch({ type: ORDER_ACTIONS.SET_CATEGORY, payload: category });
    };

    const setCurrentItem = (item) => {
        dispatch({ type: ORDER_ACTIONS.SET_ITEM, payload: item });
    };

    const setCurrentModifiers = (modifiers) => {
        dispatch({ type: ORDER_ACTIONS.SET_MODIFIERS, payload: modifiers });
    };

    const resetOrder = () => {
        clearAllData(); // Clear localStorage when resetting
        dispatch({ type: ORDER_ACTIONS.RESET_ORDER });
    };

    const clearCart = () => {
        // Do not clear all stored data to preserve customerInfo in storage
        dispatch({ type: ORDER_ACTIONS.CLEAR_CART });
    };

    const setLoading = (loading) => {
        dispatch({ type: ORDER_ACTIONS.SET_LOADING, payload: loading });
    };

    const setError = (error) => {
        dispatch({ type: ORDER_ACTIONS.SET_ERROR, payload: error });
    };

    const value = {
        ...state,
        addItemToOrder,
        removeItemFromOrder,
        updateItemQuantity,
        updateCustomerInfo,
        setCurrentStep,
        setCurrentCategory,
        setCurrentItem,
        setCurrentModifiers,
        resetOrder,
        clearCart,
        setLoading,
        setError
    };

    return (
        <OrderContext.Provider value={value}>
            {children}
        </OrderContext.Provider>
    );
}

// Custom hook to use the order context
export function useOrder() {
    const context = useContext(OrderContext);
    if (!context) {
        throw new Error('useOrder must be used within an OrderProvider');
    }
    return context;
}

export default OrderContext;
